import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { CardList } from '../../../../model/types';
import {
	beginOptimisticUiMeta,
	commitOptimisticUiMeta,
	type OptimisticId,
	type OptimisticUiMeta,
	type OptimisticUiMetaWithFlag,
	revertOptimisticUiWithFlagMeta,
} from '../../meta/optimistic-ui-meta';

let lastSprintId = 0;
const generateId = (): number => {
	lastSprintId -= 1;
	return lastSprintId;
};

export const SPRINT_CREATE_REQUEST = 'state.actions.sprints.create.SPRINT_CREATE_REQUEST' as const;

export type SprintCreateRequestAction = {
	type: typeof SPRINT_CREATE_REQUEST;
	payload: {
		temporaryCardListId: string;
	};
	meta: OptimisticUiMeta & {
		analyticsEvent: UIAnalyticsEvent;
	};
};

export const sprintCreateRequest = (
	analyticsEvent: UIAnalyticsEvent,
): SprintCreateRequestAction => ({
	type: SPRINT_CREATE_REQUEST,
	payload: {
		temporaryCardListId: String(generateId()),
	},
	meta: {
		...beginOptimisticUiMeta(),
		analyticsEvent,
	},
});

export const SPRINT_CREATE_SUCCESS = 'state.actions.sprints.create.SPRINT_CREATE_SUCCESS' as const;

export type SprintCreateSuccessAction = {
	type: typeof SPRINT_CREATE_SUCCESS;
	payload: {
		sprint: CardList;
		temporaryCardListId: string;
	};
	meta: OptimisticUiMeta & {
		analyticsEvent: UIAnalyticsEvent;
	};
};

export const sprintCreateSuccess = (
	sprint: CardList,
	temporaryCardListId: string,
	optimisticId: OptimisticId,
	analyticsEvent: UIAnalyticsEvent,
): SprintCreateSuccessAction => ({
	type: SPRINT_CREATE_SUCCESS,
	payload: {
		sprint,
		temporaryCardListId,
	},
	meta: {
		...commitOptimisticUiMeta(optimisticId),
		analyticsEvent,
	},
});

export const SPRINT_CREATE_FAILURE = 'state.actions.sprints.create.SPRINT_CREATE_FAILURE' as const;

export type SprintCreateFailureAction = {
	type: typeof SPRINT_CREATE_FAILURE;
	payload: {
		temporaryCardListId: string;
	};
	meta: OptimisticUiMetaWithFlag;
};

export const sprintCreateFailure = (
	temporaryCardListId: string,
	optimisticId: OptimisticId,
	analyticsEvent?: UIAnalyticsEvent,
): SprintCreateFailureAction => ({
	type: SPRINT_CREATE_FAILURE,
	payload: {
		temporaryCardListId,
	},
	meta: {
		...revertOptimisticUiWithFlagMeta(optimisticId),
		...(analyticsEvent ? { analyticsEvent } : {}),
	},
});

export type Action =
	| SprintCreateRequestAction
	| SprintCreateSuccessAction
	| SprintCreateFailureAction;
